import type { ReactNode } from 'react'
import { AlertCircleIcon, CheckCircleIcon, AlertTriangleIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

type AlertVariant = 'error' | 'warning' | 'success'

interface OnboardingAlertProps {
  /** The variant/type of alert */
  variant?: AlertVariant
  /** Title text (optional) */
  title?: string
  /** Main content */
  children: ReactNode
  /** Additional className */
  className?: string
}

const variantStyles: Record<AlertVariant, { bg: string; icon: string; title: string; text: string }> = {
  error: {
    bg: 'bg-[#fef3f2]',
    icon: 'text-[#f04438]',
    title: 'text-[#b42318]',
    text: 'text-[#d92d20]',
  },
  warning: {
    bg: 'bg-[#fffaeb]',
    icon: 'text-[#f79009]',
    title: 'text-[#b54708]',
    text: 'text-[#dc6803]',
  },
  success: {
    bg: 'bg-[#ecfdf3]',
    icon: 'text-[#12b76a]',
    title: 'text-[#027a48]',
    text: 'text-[#039855]',
  },
}

const variantIcons: Record<AlertVariant, typeof AlertCircleIcon> = {
  error: AlertCircleIcon,
  warning: AlertTriangleIcon,
  success: CheckCircleIcon,
}

export function OnboardingAlert({
  variant = 'error',
  title,
  children,
  className,
}: OnboardingAlertProps) {
  const styles = variantStyles[variant]
  const Icon = variantIcons[variant]

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg p-3',
        styles.bg,
        className
      )}
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0', styles.icon)} />
      <div>
        {title && (
          <p className={cn('text-sm font-medium', styles.title)}>{title}</p>
        )}
        <div className={cn('text-sm', title && 'mt-1', styles.text)}>
          {children}
        </div>
      </div>
    </div>
  )
}
